import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { map, tap, timeout } from 'rxjs/operators';
import { filter, first, firstValueFrom, interval, Observable, skip, Subscription } from 'rxjs';
import { STATE, STATUS, STATUS_NUMBERS, UPDATE_INTERVAL } from '../avatar.constants';
import { Position } from '@capacitor/geolocation';
import { AlertController, AlertOptions, ModalController, ModalOptions } from '@ionic/angular/standalone';
import Locator, { LocatorRunning } from '@plugins/locator/locator.plugin';
import {
  Client,
  CoordinateCheckStatusResponse,
  CoordinateRequest,
  CoordinateUpdateRequest,
  Location,
  PositionResponse,
  StoreUserLocationRequest,
  StoreUserLocationRequestSource,
  UpdateVideoLocationRequest,
  UpdateVideoLocationRequestLocation_source
} from '@api/client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { WebSocketController } from '@services/web-socket.controller';
import { AuthService, ParsedUser } from '@services/auth.service';
import { LoadingService } from '@services/loading.service';
import { PlatformService } from '@services/platform.service';
import { PhoneInfoService } from './phone-info.service';
import { AppStateChangeService } from '@services/app-state-change.service';
import { HttpStatusCode } from '@angular/common/http';
import { OnlineStateService } from '@services/online-state.service';
import { NotificationService } from '@services/notification.service';
import { environment } from '@environments/environment';
import { TutorialControlComponent } from '../modals/tutorial-control/tutorial-control.component';
import { PhoneInfoRequestExt, SOCKET_EVENT_NAMES } from '@services/web-socket.interfaces';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { LocationAcquisitionService, LocationResult } from './location-acquisition.service';

@Injectable({
  providedIn: 'root'
})
export class AvatarService implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(Client);
  private readonly toastService = inject(ToastService);
  private readonly alertController = inject(AlertController);
  private readonly translate = inject(TranslateService);
  private readonly webSocketController = inject(WebSocketController);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly platformService = inject(PlatformService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalController = inject(ModalController);
  private readonly diagnostic = inject(Diagnostic);
  private readonly locationAcquisition = inject(LocationAcquisitionService);

  public isOnline: boolean = false;
  public isCompassAvailable: boolean = false;
  public isExecutorCompassAvailable: boolean = false;
  public inProcessStatusSwitching: boolean = false;
  private prevSendMarkerLatitude: number | null = null;
  private prevSendMarkerLongitude: number | null = null;
  private prevSendUserLatitude: number | null = null;
  private prevSendUserLongitude: number | null = null;
  private currentVideoId: number | null = null;
  private parsedUserSubscription: Subscription | null = null;
  // private watchPositionSubscription: string | null = null;
  private positionSubscription: Subscription | null = null;
  private useTestCoordinates: boolean = false;
  private testCoordinates$: Observable<{ latitude: number, longitude: number }>;
  private latitudeDegreesInMeter: number = 0.0000089831 * 2;
  private longitudeDegreesInMeter: number = 0.0000089831;
  private inStatusSettingProcess: boolean = false;
  private inStatusCheckingProcess: boolean = false;
  private alertToPromptStatusSwitch: HTMLIonAlertElement;
  public get isLocationWatching(): boolean {
    return this.locationAcquisition.isLocationWatching;
  }

  public set isLocationWatching(value: boolean) {
    this.locationAcquisition.setLocationWatching(value);
  }

  constructor() {
    // Обновляем снимок разрешений при старте
    this.locationAcquisition.refreshPermissionSnapshot().catch(() => {});
    this.subscribeToEvents();
  }

  ngOnDestroy(): void {
    if (this.parsedUserSubscription) {
      this.parsedUserSubscription.unsubscribe();
      this.parsedUserSubscription = null;
    }
    if (this.positionSubscription) {
      this.positionSubscription.unsubscribe();
      this.positionSubscription = null;
    }
  }

  private get isGpsSupported(): boolean {
    return (this.platformService.isAndroidApp || this.platformService.isIosApp) || (this.platformService.isAndroidWeb || this.platformService.isIosWeb);
  }

  public get hasValidCoordinates(): boolean {
    return this.locationAcquisition.hasValidCoordinates;
  }

  public get currentCoordinatePrecision(): number {
    return this.locationAcquisition.currentCoordinatePrecision;
  }

  private subscribeToEvents(): void {
    this.webSocketController.avatarEvent.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (res: any): Promise<void> => {
      switch (res?.type) {
        case SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS:
          // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_SYNC_STATUS}'` + '\x1b[0m', res.data);

          if (!res?.data) return console.error('🔴 syncStatus, res.data is not defined');

          await this.syncStatus(res.data?.is_online);
          break;
        case SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED:
          // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_STATUS_UPDATED}'` + '\x1b[0m', res.data);

          if (!res?.data) return console.error('🔴 status, res.data is not defined');

          if ((res.data?.userId === this.authService.parsedToken?.userId)
            && (res.data?.status == STATUS_NUMBERS.OFFLINE)
            && this.isOnline
            && !this.inProcessStatusSwitching) {
            await this.syncStatus(false);
          }
          break;
        case SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST:
          // console.log('\x1b[35m' + `Socket event '${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST}'` + '\x1b[0m', res?.info);
          await this.handlePhoneInfoRequest(res?.info);
          break;
      }
    });

    if (!this.platformService.isDevice && environment.production) return;

    this.appStateChangeService.appStateChange$.pipe(
      filter((isActive: boolean): boolean => isActive),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (): Promise<void> => {
      await this.checkStatus();
    });

    this.onlineStateService.isOffline$.pipe(
      skip(1),
      filter((isOffline: boolean): boolean => !isOffline),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (): Promise<void> => {
      await this.checkStatus();
    });
  }

  private async handlePhoneInfoRequest(info: PhoneInfoRequestExt): Promise<void> {
    if (!info?.from_id) {
      console.error(`Received incorrect socket data for ${SOCKET_EVENT_NAMES.AVATAR_PHONE_INFO_REQUEST}!`);
      return;
    }

    this.webSocketController.emitAvatarPhoneInfoResponse(
      info?.from_id,
      this.phoneInfoService.myBatterySubject.value.level,
      this.phoneInfoService.myBatterySubject.value.isCharging,
      await this.phoneInfoService.getDeviceModelWithOS(),
    );
  }

  //#region Permission Location

  // async checkLocationPermission() {
  //   console.log('checkPermissionsGPSStatus');
  //   await this.diagnostic.isLocationAuthorized(
  //     async (authorized: any) => {
  //       if (!authorized) {
  //         console.warn('Permissions location is not allowed');
  //         await this.showAlertToEnableApplicationPermissionsGPS();
  //       }
  //     },
  //     async (error: any) => {
  //       await this.toastService.warning(this.translate.instant('avatar.avatarService.toasts.gps.errorCheckingPermissions'));
  //       console.error(error);
  //     }
  //   )
  // }

  public async checkLocationPermissionStatusWithBrowser(callback: Function = null, options: { silent?: boolean } = {}): Promise<void> {
    const { silent = false } = options;

    try {
      const status = await this.locationAcquisition.getPermissionStatus();

      switch (status.permission) {
        case 'granted':
          if (typeof callback === 'function') await callback();
          break;
        case 'prompt':
        case 'unknown':
          if (silent) throw new Error('LOCATION_PERMISSION_PROMPT');
          await this.showDescriptionForRequestLocationPermission(
            (): Promise<void> => this.requestBrowserLocationPermission(callback),
          );
          break;
        case 'denied':
        default:
          if (silent) throw new Error('LOCATION_PERMISSION_DENIED');
          await this.toastService.warning(this.translate.instant('avatar.avatarService.toasts.gps.allowBrowserGeolocation'));
          break;
      }
    } catch (error) {
      const errorMessage: string = this.translate.instant('avatar.avatarService.toasts.gps.errorCheckingPermissions');
      if (!silent) {
        console.error(errorMessage);
        await this.toastService.error(errorMessage);
      }
      throw error;
    }
  }

  public async checkLocationPermissionStatusWithDevice(callback: Function = null, options: { silent?: boolean } = {}): Promise<void> {
    const { silent = false } = options;

    if (typeof window === 'undefined') return;

    try {
      const status = await this.locationAcquisition.getPermissionStatus();
      switch (status.permission) {
        case 'granted':
          if (typeof callback === 'function') await callback();
          break;
        case 'prompt':
        case 'unknown':
          if (silent) throw new Error('LOCATION_PERMISSION_PROMPT');
          await this.showDescriptionForRequestLocationPermission(() => this.requestLocationPermission(callback));
          break;
        case 'denied':
        default:
          if (silent) throw new Error('LOCATION_PERMISSION_DENIED');
          await this.showAlertToEnableLocationPermissions(callback);
          break;
      }
    } catch (error) {
      if (!silent) {
        await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorCheckingPermissions'));
        console.error(error);
      }
      throw new Error(error);
    }
  }

  private async requestLocationPermission(callback: Function = null): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      const status = await this.locationAcquisition.requestPermissions();
      if (status.permission === 'granted') {
        if (typeof callback === 'function') await callback();
      } else {
        await this.showAlertToEnableLocationPermissions(callback);
      }
    } catch (error) {
      console.error(error);
      await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorRequestingGeolocation'));
      throw new Error(error);
    }
  }

  async showDescriptionForRequestLocationPermission(callback: Function = null): Promise<void> {
    const opts: AlertOptions = {
      header: this.translate.instant('avatar.walkDetails.alert.header'),
      message: this.translate.instant('avatar.walkDetails.alert.message'),
      buttons: [
        {
          text: this.translate.instant('avatar.walkDetails.alert.ok'),
          role: 'confirm',
          handler: async () => {
            if (typeof callback === 'function') await callback();
          }
        }
      ]
    };
    const alert: HTMLIonAlertElement = await this.alertController.create(opts);
    await alert?.present();
    await alert.onDidDismiss();
  }

  public async showAlertToEnableLocationPermissions(callback: Function = null): Promise<void> {
    const opts: AlertOptions = {
      header: this.translate.instant('avatar.avatarService.alerts.gps.headers.permissionsGps'),
      buttons: [
        {
          text: this.translate.instant('avatar.avatarService.alerts.cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('avatar.avatarService.alerts.settings'),
          role: 'confirm',
          handler: async () => {
            if (typeof window !== 'undefined') {
              await this.diagnostic.switchToSettings();
            }
            this.appStateChangeService.appResume$.pipe(
              first((isActive: boolean) => isActive),
              takeUntilDestroyed(this.destroyRef),
            ).subscribe(async (): Promise<void> => {
              if (typeof callback === 'function') await callback();
            });
          },
        }
      ],
    };
    const alert: HTMLIonAlertElement = await this.alertController.create(opts);
    await alert?.present();
    await alert.onDidDismiss();
  }

  //#endregion Permission Location

  //#region Enabled Location

  public async checkLocationEnabled(callback: Function = null, options: { silent?: boolean } = {}): Promise<void> { // для Android and iOS, но без источника местоположения
    if (typeof window === 'undefined') return;

    const { silent = false } = options;

    try {
      const enabled: boolean = await this.diagnostic.isLocationEnabled();

      if (!enabled) {
        console.warn('Geolocation is disabled');
        if (!silent) {
          await this.showAlertToEnableSystemLocation(callback);
        } else {
          throw new Error('LOCATION_DISABLED');
        }
      } else {
        console.log('Geolocation is enabled');
        if (typeof callback === 'function') await callback();
      }
    } catch (error) {
      console.error(error);
      if (!silent) {
        await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorCheckingGeolocation'));
      }
      throw new Error(error);
    }
  }

  // public checkGpsLocationEnabled(callback: Function = null) { // более точное чем checkLocationEnabled определение состояния GPS для Android, но iOS не поддерживает данный плагин
  //   this.diagnostic.isGpsLocationEnabled(
  //     async (enabled: any) => {
  //       if (!enabled) {
  //         console.warn('Geolocation is disabled');
  //         await this.showAlertToEnableSystemGPS(callback);
  //       } else {
  //         console.log('Geolocation is enabled');
  //         if (typeof callback === 'function') await callback();
  //       }
  //     },
  //     async (error: any) => {
  //       console.error(error);
  //       await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorCheckingGeolocation'));
  //       throw new Error(error);
  //     }
  //   );
  // }

  public async showAlertToEnableSystemLocation(callback: Function = null): Promise<void> {
    const opts: AlertOptions = {
      header: this.translate.instant('avatar.avatarService.alerts.gps.headers.systemGps'),
      buttons: [
        {
          text: this.translate.instant('avatar.avatarService.alerts.cancel'),
          role: 'cancel',
        },
        {
          text: this.translate.instant('avatar.avatarService.alerts.settings'),
          role: 'confirm',
          handler: async () => {
            if (this.platformService.isAndroid) this.diagnostic.switchToLocationSettings();
            if (this.platformService.isIos) await this.diagnostic.switchToSettings();

            this.appStateChangeService.appResume$.pipe(
              first((isActive: boolean) => isActive),
              takeUntilDestroyed(this.destroyRef),
            ).subscribe(async (): Promise<void> => {
              if (typeof callback === 'function') await callback();
            });
          },
        },
      ],
    };
    const alert: HTMLIonAlertElement = await this.alertController.create(opts);
    await alert?.present();
    await alert.onDidDismiss();
  }
  //#endregion Enabled Location

  //#region Location

  public setCurrentVideoId(videoId: number | null): void {
    this.currentVideoId = videoId;
  }

  private isSilentLocationError(error: any): boolean {
    if (!error) return false;
    const message: string = typeof error === 'string'
      ? error
      : (error?.message || String(error));

    return [
      'LOCATION_PERMISSION_DENIED',
      'LOCATION_PERMISSION_PROMPT',
      'LOCATION_DISABLED',
    ].some((code: string): boolean => message.includes(code));
  }

  public async getLocationByIPAddress(options: { silent?: boolean; allowGpsFallback?: boolean; useFallbackOnFail?: boolean } = {}): Promise<void> {
    const {
      silent = false,
      allowGpsFallback = true,
      useFallbackOnFail = true,
    } = options;

    const canUseGpsFallback: boolean = allowGpsFallback && await this.isSystemLocationEnabledSafely();

    if (this.onlineStateService.isOffline) {
      this.onlineStateService.isOffline$.pipe(
        skip(1),
        first((isOffline: boolean): boolean => !isOffline),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(async (): Promise<void> => {
        await this.getLocationByIPAddress(options);
      });
      return;
    }

    try {
      const locationByIP: PositionResponse = await firstValueFrom(this.client.coordinateGetPosition());

      if (locationByIP?.latitude == null || locationByIP?.longitude == null) {
        throw new Error('Last location is not defined');
      }

      this.locationAcquisition.updateCoordinates(locationByIP.latitude, locationByIP.longitude, StoreUserLocationRequestSource.Ip);
      const stored = this.locationAcquisition.getStoredCoordinates();
      if (stored) {
        this.sendUserLocation(stored.latitude, stored.longitude, StoreUserLocationRequestSource.Ip).then();
      }
    } catch (error) {
      if (!silent) {
        console.error('Failed to receive location by IP:', error);
      }

      const canRequestPreciseLocation: boolean = await this.hasActiveLocationPermission();
      let shouldShowError: boolean = false;

      if (canUseGpsFallback && canRequestPreciseLocation) {
        try {
          await this.getFirstCoordinate();
          return;
        } catch (fallbackError) {
          if (!silent && !this.isSilentLocationError(fallbackError)) {
            console.error('Fallback geolocation acquisition failed:', fallbackError);
          }
          shouldShowError = true;
        }
      }

      if (!this.hasValidCoordinates && useFallbackOnFail) {
        this.locationAcquisition.useFallbackCoordinates();
      }

      if (!this.hasValidCoordinates) {
        shouldShowError = true;
      }

      if (shouldShowError && !silent) {
        await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorReceivingLastLocation'));
      }
    }
  }

  public async ensureInitialCoordinates(): Promise<void> {
    await this.locationAcquisition.ensureInitialCoordinates();
  }

  public async getFirstCoordinate(): Promise<Position | GeolocationPosition> {
    return this.locationAcquisition.getFirstCoordinate();
  }

  public async getCoordinateForCenter(): Promise<Position | GeolocationPosition> {
    return this.locationAcquisition.getCoordinateForCenter();
  }

  public getLastSavedPosition(): Position | GeolocationPosition | null {
    return this.locationAcquisition.getLastSavedPosition();
  }

  public async getCurrentPosition(options: { silent?: boolean } = {}): Promise<void> {
    const { silent = false } = options;
    try {
      const position = await this.locationAcquisition.getCurrentPosition({ silent, updateState: false });
      this.handleGetPosition(position, StoreUserLocationRequestSource.Gps);
    } catch (error) {
      if (!silent) {
        console.error(error);
        await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorGettingCurrentLocation'));
      }
      throw error;
    }
  }

  private async isSystemLocationEnabledSafely(): Promise<boolean> {
    return this.locationAcquisition.isSystemLocationEnabled();
  }

  private handleGetPosition(newPosition: Position | GeolocationPosition, source?: StoreUserLocationRequestSource): void {
    const resolvedSource: StoreUserLocationRequestSource = source ?? StoreUserLocationRequestSource.Gps;
    const coords = this.locationAcquisition.updateFromPosition(newPosition, resolvedSource);
    if (!coords) return;

    if (this.isOnline) {
      this.sendMarkerLocation(coords.latitude, coords.longitude).then();
    }
    this.sendUserLocation(coords.latitude, coords.longitude, resolvedSource).then();
  }

  private async hasActiveLocationPermission(): Promise<boolean> {
    return this.locationAcquisition.hasActiveLocationPermission();
  }

  public async showPermissionsRationaleIfNeeded(shouldShow: boolean): Promise<void> {
    if (!shouldShow) return;
    await this.showCombinedPermissionsAlert();
  }

  private async showCombinedPermissionsAlert(): Promise<void> {
    const opts: AlertOptions = {
      message: this.translate.instant('avatar.avatarService.alerts.permissionsBeforeStream.message'),
      buttons: [
        {
          text: this.translate.instant('avatar.avatarService.alerts.permissionsBeforeStream.confirm'),
          role: 'confirm',
        },
      ],
      backdropDismiss: false,
    };
    const alert: HTMLIonAlertElement = await this.alertController.create(opts);
    await alert.present();
    await alert.onDidDismiss();
  }

  public async resolveStreamCoordinate(): Promise<Position | GeolocationPosition | null> {
    const lastPosition: Position | GeolocationPosition | null = this.getLastSavedPosition();
    if (lastPosition) {
      return lastPosition;
    }

    try {
      return await this.locationAcquisition.getFirstCoordinate();
    } catch (error) {
      console.warn('Unable to retrieve geolocation for stream:', error);
      return null;
    }
  }

  public async getCoordinateForStream(): Promise<Position | GeolocationPosition | null> {
    try {
      return await this.locationAcquisition.getFirstCoordinate();
    } catch (_error) {
      return null;
    }
  }

  public async requestPermissions(): Promise<void> {
    try {
      await Locator.requestPermissions();
    } catch (error) {
      if (error.message === 'Cancelled') {
        console.warn('🟠 requestPermissions, error: ', error);
      } else {
        console.error('🔴 requestPermissions, error: ', error);
        await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.permissions.errorRequestPermissions'));
      }
      throw error;
    }
  }

  public async toggleLocatorService(state: STATE): Promise<void> {
    try {
      const isServiceRunning: LocatorRunning = await Locator.isServiceRunning();

      if (state == STATE.ACTIVATE && !isServiceRunning.result) await this.startLocatorService();
      if (state == STATE.DEACTIVATE && isServiceRunning.result) await this.stopLocatorService();
    } catch (error) {
      console.error('🔴 toggleLocatorService, error: ', error);
    }
  }

  public async startLocatorService(): Promise<void> {
    try {
      await Locator.startService();
    } catch (error) {
      console.error('🔴 startLocatorService, error: ', error);
      throw error;
    }
  }

  private async stopLocatorService(): Promise<void> {
    try {
      await Locator.stopService();
    } catch (error) {
      console.error('🔴 stopLocatorService, error: ', error);
      throw error;
    }
  }

  public async toggleLocationWatching(state: STATE): Promise<void> {
    switch (state) {
      case STATE.ACTIVATE:
        if (this.useTestCoordinates) {
          this.watchTestCoordinates();
        } else if (!this.positionSubscription) {
          this.positionSubscription = this.locationAcquisition.watchPosition(UPDATE_INTERVAL.MY_GPS).subscribe({
            next: (position: Position | GeolocationPosition) => this.handleGetPosition(position, StoreUserLocationRequestSource.Gps),
            error: async (error: any) => {
              console.error('Location watching error:', error);
              await this.toastService.error(this.translate.instant('avatar.avatarService.toasts.gps.errorWatchingLocation'));
            }
          });
        }

        if (this.platformService.isAndroid && this.platformService.isDevice) {
          await this.toggleLocatorService(state); // native service for Android
        }

        this.isLocationWatching = true;
        await this.phoneInfoService.toggleBatteryChargeWatching(state);
        break;
      case STATE.DEACTIVATE:
        if (this.positionSubscription) {
          this.positionSubscription.unsubscribe();
          this.positionSubscription = null;
        }

        if (this.platformService.isAndroid && this.platformService.isDevice) {
          await this.toggleLocatorService(state); // native service for Android
        }

        this.isLocationWatching = false;
        await this.phoneInfoService.toggleBatteryChargeWatching(state);
        break;
    }
  }

  public async sendMarkerLocation(latitude: number, longitude: number): Promise<void> {
    if (this.onlineStateService.isOffline || !this.appStateChangeService.appIsActive) return;
    if (!this.isOnline) {
      console.log('🔴 User is offline');
      return;
    }

    if (this.prevSendMarkerLatitude == latitude && this.prevSendMarkerLongitude == longitude) {
      // console.log('Marker location not sent because it has not changed'/* ,{latitude, longitude} */);
      return;
    }

    try {
      const location: Location = new Location({
        type: 'Point',
        coordinates: [longitude, latitude] // longitude must be first
      });
      const request: CoordinateUpdateRequest = new CoordinateUpdateRequest({
        location,
      });
      await firstValueFrom(this.client.coordinatePut(request));

      if (!environment.production) console.info('📍 Send new location:', {latitude, longitude});

      this.prevSendMarkerLatitude = latitude;
      this.prevSendMarkerLongitude = longitude;
    } catch (error) {
      console.error('Error sending location: ', error?.result?.message || error?.result?.error || error);

      if (error.status == HttpStatusCode.Forbidden || error.status == HttpStatusCode.NotFound) {
        await this.syncStatus(false);
      }
    }
  }

  public async sendUserLocation(latitude: number, longitude: number, source?: StoreUserLocationRequestSource, videoId?: number): Promise<void> {
    if (!this.authService.isAuthenticated) return;

    if (this.prevSendUserLatitude == latitude && this.prevSendUserLongitude == longitude) {
      // console.log('User location not sent because it has not changed'/* ,{ latitude, longitude} */);
      return;
    }

    const resolvedVideoId: number | null = videoId ?? this.currentVideoId;

    try {
      if (resolvedVideoId) {
        const locationSource = source === StoreUserLocationRequestSource.Ip
          ? UpdateVideoLocationRequestLocation_source.Ip
          : (source === StoreUserLocationRequestSource.Gps
            ? UpdateVideoLocationRequestLocation_source.Gps
            : undefined);
        const body = new UpdateVideoLocationRequest({
          long: longitude,
          lat: latitude,
          location_source: locationSource,
        });
        await firstValueFrom(this.client.videosUpdateLocation(resolvedVideoId, body));
      } else {
        const body = new StoreUserLocationRequest({
          long: longitude,
          lat: latitude,
          source,
        });
        await firstValueFrom(this.client.userLocation(body));
      }

      this.prevSendUserLatitude = latitude;
      this.prevSendUserLongitude = longitude;
    } catch (error) {
      console.error('Error sending user location: ', error);
    }
  }

  //#region only for the test
  public generateTestCoordinates(latStart: number, lonStart: number, step: number = 1): Observable<any> {
    let latitude: number = latStart + Math.random() / 100;
    let longitude: number = lonStart + Math.random() / 100;

    return interval(UPDATE_INTERVAL.TEST_GPS).pipe(
      map((): {
        latitude: number;
        longitude: number;
      } => {
        latitude -= this.latitudeDegreesInMeter * step;
        longitude += this.longitudeDegreesInMeter * step;
        return {latitude, longitude};
      })
    );
  }

  private watchTestCoordinates(): void {
    const latStart: number = 43.24065; // Алматы (замени на свои координаты)
    const lonStart: number = 76.87700; // Алматы (замени на свои координаты)
    const step: number = 25; // шаг примерно 25 метров (замени на нужный тебе шаг)
    const TIMEOUT: number = 5; // движение в течение 5 минут (замени на нужное тебе время)

    this.testCoordinates$ = this.generateTestCoordinates(latStart, lonStart, step).pipe(
      timeout(60_000 * TIMEOUT),
    );

    this.testCoordinates$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(async (coordinates: {
      latitude: number,
      longitude: number
    }): Promise<void> => {
      if (coordinates) {
        // console.log('Next test coordinates: ', coordinates);
        this.locationAcquisition.updateCoordinates(coordinates.latitude, coordinates.longitude, StoreUserLocationRequestSource.Gps, {
          persist: false,
        });
        const stored = this.locationAcquisition.getStoredCoordinates();
        if (stored) {
          await this.sendMarkerLocation(stored.latitude, stored.longitude);
        }
      }
    });
  }

  //#endregion only for the test

  //#endregion Location

  //#region Status

  private async checkStatus(): Promise<void> {
    if (!this.platformService.isDevice && environment.production) return;

    if (this.alertToPromptStatusSwitch || this.inStatusCheckingProcess || this.inStatusSettingProcess) return;

    if (this.platformService.isDevice && this.platformService.isAndroid) {
      await this.checkStatusOnAndroid();
    }

    if ((this.platformService.isDevice && !this.platformService.isAndroid) || !this.platformService.isDevice) {
      await this.checkStatusOnNonAndroid();
    }
  }

  private async checkStatusOnAndroid(): Promise<void> {
    try {
      this.inStatusCheckingProcess = true;
      const isServiceRunning: LocatorRunning = await Locator.isServiceRunning();

      if (!this.authService.isAuthenticated && isServiceRunning.result) {
        console.warn('🟠 Locator service running, but user is not authenticated');
        /* if the location is only sent by the native service */
        // await this.stopLocatorService();
        // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.OFFLINE, STATE.DEACTIVATE);

        /* else if the frontend also sends the location, in parallel with the native service */
        await this.toggleLocationWatching(STATE.DEACTIVATE);
        this.setLocalStatus(STATUS.OFFLINE);

        this.inStatusCheckingProcess = false;
        return;
      }

      if (this.authService.isAuthenticated) {
        const {isOnline} = await this.checkStatusOnBackend();

        // if (isOnline && isServiceRunning.result) {
        //   try {
        //     await this.requestPermissions(); // Виснет на этом месте
        //     await this.notificationService.registerPushIfNeedAndSendPushToken();
        //     await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.ONLINE, STATE.ACTIVATE);
        //   } catch (error) {
        //     await this.toggleLocationWatching(STATE.DEACTIVATE);
        //     await this.setOfflineRemoteStatus();
        //   }
        //   return;
        // }

        if (isOnline && isServiceRunning.result) {
          console.warn('🟢 Locator service running and user is online');
          await this.notificationService.checkPushPermissionsWithDevice(
            async (): Promise<void> => {
              await this.toggleLocationWatching(STATE.DEACTIVATE);
              await this.setOfflineRemoteStatus();
              this.inStatusCheckingProcess = false;
            },
            async (): Promise<void> => {
              await this.notificationService.registerPushIfNeedAndSendPushToken();
              /* if the location is only sent by the native service */
              // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.ONLINE, STATE.ACTIVATE);

              /* else if the frontend also sends the location, in parallel with the native service */
              await this.toggleLocationWatching(STATE.ACTIVATE);
              this.setLocalStatus(STATUS.ONLINE);
              this.emitAvatarSyncStatus(true);

              this.inStatusCheckingProcess = false;
            }
          );
          return;
        }

        if (isOnline && !isServiceRunning.result) {
          console.warn('🟠 Locator service is not running, but user is online');
          /* if the location is only sent by the native service */
          // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.OFFLINE, STATE.DEACTIVATE);

          /* else if the frontend also sends the location, in parallel with the native service */
          await this.toggleLocationWatching(STATE.DEACTIVATE);
          this.setLocalStatus(STATUS.OFFLINE);

          await this.showAlertToSwitchStatus();
          this.inStatusCheckingProcess = false;
          return;
        }

        if (!isOnline && isServiceRunning.result) {
          console.warn('🟠 Locator service running, but user is offline');
          /* if the location is only sent by the native service */
          // await this.stopLocatorService();
          // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.OFFLINE, STATE.DEACTIVATE);

          /* else if the frontend also sends the location, in parallel with the native service */
          await this.toggleLocationWatching(STATE.DEACTIVATE);
          this.setLocalStatus(STATUS.OFFLINE);

          this.inStatusCheckingProcess = false;
          return;
        }

        if (!isOnline && !isServiceRunning.result) {
          console.warn('🟢 Locator service is not running and user is offline');
          /* if the location is only sent by the native service */
          // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.OFFLINE, STATE.DEACTIVATE);

          /* else if the frontend also sends the location, in parallel with the native service */
          await this.toggleLocationWatching(STATE.DEACTIVATE);
          this.setLocalStatus(STATUS.OFFLINE);

          this.inStatusCheckingProcess = false;
        }
      }
    } catch (error) {
      console.error('🔴 Check status error: ', error);
      this.inStatusCheckingProcess = false;
    }
  }

  private emitAvatarSyncStatus(isOnline: boolean): void {
    if (this.webSocketController.isSocketInitedSubject) {
      this.webSocketController.emitAvatarSyncStatus(isOnline);
      return;
    }

    this.webSocketController.isSocketInitedSubject.pipe(
      first((isSocketInited: boolean): boolean => isSocketInited),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((): void => {
      this.webSocketController.emitAvatarSyncStatus(isOnline);
    });
  }

  // private async updateLocalStatusAndBatteryWatchingStateIfNeeded(needLocalStatus: STATUS.ONLINE | STATUS.OFFLINE, needBatteryWatchingState: STATE): Promise<void> {
  //   if (this.isOnline !== (needLocalStatus === STATUS.ONLINE)) {
  //     this.setLocalStatus(needLocalStatus);
  //
  //     if (this.isOnline) {
  //       this.emitAvatarSyncStatus(true);
  //     }
  //   }
  //
  //   if (this.phoneInfoService.isWatchBattery !== (needBatteryWatchingState === STATE.ACTIVATE)) {
  //     await this.phoneInfoService.toggleBatteryChargeWatching(needBatteryWatchingState);
  //   }
  // }

  private async showAlertToSwitchStatus(): Promise<void> {
    if (this.alertToPromptStatusSwitch || this.inStatusSettingProcess) return;

    const opts: AlertOptions = {
      header: this.translate.instant('avatar.avatarService.alerts.isOnline.header'),
      buttons: [
        {
          text: this.translate.instant('avatar.avatarService.alerts.isOnline.buttons.cancel'),
          role: 'cancel',
          handler: async () => {
            try {
              await this.setOfflineRemoteStatus();
            } catch (error) {
              console.error('🔴 Error switch offline: ', error);
            }
          },
        },
        {
          text: this.translate.instant('avatar.avatarService.alerts.isOnline.buttons.confirm'),
          role: 'confirm',
          handler: async () => {
            try {
              await this.alertToPromptStatusSwitch?.dismiss();

              await this.requestPermissions();
              await this.notificationService.registerPushIfNeedAndSendPushToken();
              /* if the location is only sent by the native service */
              // await this.updateLocalStatusAndBatteryWatchingStateIfNeeded(STATUS.ONLINE, STATE.ACTIVATE);
              // await this.toggleLocationWatching(STATE.ACTIVATE);

              /* else if the frontend also sends the location, in parallel with the native service */
              await this.toggleLocationWatching(STATE.ACTIVATE);
              this.setLocalStatus(STATUS.ONLINE);
              this.emitAvatarSyncStatus(true);
            } catch (error) {
              console.error('🔴 Error switch online: ', error);
              await this.setOfflineRemoteStatus();
            }
          },
        }
      ],
      backdropDismiss: false
    };
    this.alertToPromptStatusSwitch = await this.alertController.create(opts);
    this.notificationService.closeAlertNotificationPermissionsEvent.emit();
    await this.alertToPromptStatusSwitch?.present();
    await this.alertToPromptStatusSwitch?.onDidDismiss();
    this.alertToPromptStatusSwitch = null;
  }

  private async checkStatusOnBackend(): Promise<{ isOnline: boolean, deviceId: number }> {
    const response: CoordinateCheckStatusResponse = await firstValueFrom(this.client.coordinateCheckStatus());
    return {
      isOnline: response?.status && response.status !== STATUS_NUMBERS.OFFLINE,
      deviceId: response?.deviceId // not used
    };
  }

  private async checkStatusOnNonAndroid(): Promise<void> {
    if (!this.platformService.isDevice && environment.production) return;

    if (!this.authService.isAuthenticated) {
      if (!this.parsedUserSubscription) {
        this.parsedUserSubscription = this.authService.parsedUser$.pipe(
          first((parsedUser: ParsedUser): boolean => !!parsedUser),
          tap(async (): Promise<void> => {
            await this.checkStatusOnNonAndroid();
          })
        ).subscribe();
      }
      return;
    }

    try {
      const {isOnline} = await this.checkStatusOnBackend();

      if (!isOnline) {
        if (!this.isLocationWatching) {
          // console.log('🟢 Location watching already stopped');
          return;
        }

        if (this.isLocationWatching) {
          await this.toggleLocationWatching(STATE.DEACTIVATE);
          this.setLocalStatus(STATUS.OFFLINE);
          return;
        }
      }

      if (this.platformService.isDevice && !this.platformService.isAndroid) {
        if (this.isLocationWatching) {
          // console.log('🟢 Location watching already running');
          await this.notificationService.checkPushPermissionsWithDevice(
            async (): Promise<void> => {
              await this.toggleLocationWatching(STATE.DEACTIVATE);
              await this.setOfflineRemoteStatus();
            },
            async (): Promise<void> => await this.notificationService.registerPushIfNeedAndSendPushToken()
          );
          return;
        }

        if (!this.isLocationWatching) {
          await this.notificationService.checkPushPermissionsWithDevice(
            async (): Promise<void> => await this.setOfflineRemoteStatus(),
            async (): Promise<void> => this.checkLocationPermissionStatusWithDevice(
              async (): Promise<void> => await this.checkLocationEnabled(
                async (): Promise<void> => {
                  await this.notificationService.registerPushIfNeedAndSendPushToken();
                  await this.toggleLocationWatching(STATE.ACTIVATE);
                  this.setOnlineLocalStatus();
                }
              )
            )
          );
          return;
        }
      }

      if (!this.platformService.isDevice) {
        if (this.isLocationWatching) {
          // console.log('Location watching already running');
          await this.notificationService.checkNotificationPermissionStatusWithBrowser(
            async (): Promise<void> => {
              await this.toggleLocationWatching(STATE.DEACTIVATE);
              await this.setOfflineRemoteStatus();
            },
            async (): Promise<void> => await this.notificationService.registerPushIfNeedAndSendPushToken()
          );
          return;
        }

        if (!this.isLocationWatching) {
          await this.notificationService.checkNotificationPermissionStatusWithBrowser(
            async (): Promise<void> => await this.setOfflineRemoteStatus(),
            async (): Promise<void> => await this.checkLocationPermissionStatusWithBrowser(
              async (): Promise<void> => {
                await this.notificationService.initFirebaseMessagingIfNeedAndSendToken();
                await this.toggleLocationWatching(STATE.ACTIVATE);
                this.setOnlineLocalStatus();
              }
            )
          );
        }
      }
    } catch (error) {
      console.error('Check status error: ', error);
    }
  }

  private async syncStatus(isOnline: boolean): Promise<void> {
    let isServiceRunning: LocatorRunning = {result: false};

    if (this.platformService.isDevice && this.platformService.isAndroid) {
      if (!isOnline) {
        await this.alertToPromptStatusSwitch?.dismiss();
      }
      isServiceRunning = await Locator.isServiceRunning();
    }

    if (isServiceRunning.result || this.isLocationWatching) {
      const keyMessage: string =
        (isOnline)
          ? 'avatar.avatarService.toasts.gps.transmittedAnotherDevice'
          : 'avatar.avatarService.toasts.gps.noTransmitted';
      await this.toastService.warning(this.translate.instant(keyMessage));
      await this.toggleLocationWatching(STATE.DEACTIVATE);
      this.setLocalStatus(STATUS.OFFLINE);
    }
  }

  public setLocalStatus(status: STATUS.ONLINE | STATUS.OFFLINE): void {
    switch (status) {
      case STATUS.ONLINE:
        this.isOnline = true;
        break;
      case STATUS.OFFLINE:
        this.isOnline = false;
        break;
    }
  }

  public setOnlineLocalStatus(): void {
    this.setLocalStatus(STATUS.ONLINE);
    this.emitAvatarSyncStatus(true);
  }

  private setOfflineLocalStatus(): void {
    this.setLocalStatus(STATUS.OFFLINE);
    this.emitAvatarSyncStatus(false);
  }

  public async setOnlineRemoteStatus(walkRate: number,
                                     walkDescription: string,
                                     // walkIsAllowFrontCamera: boolean,
                                     // walkFrontCameraPrice: boolean,
                                     // walkIsAllowControlMovement: boolean,
                                     walkIsAllowFreeMode: boolean): Promise<void> {
    try {
      await this.loadingService.start();

      const coordinatePair = this.locationAcquisition.getStoredCoordinates();

      if (!coordinatePair) {
        throw new Error('Unable to resolve coordinates for remote status update');
      }

      const location: Location = new Location({
        type: 'Point',
        coordinates: [coordinatePair.longitude, coordinatePair.latitude] // longitude must be first
      });
      const request: CoordinateRequest = new CoordinateRequest({
        location,
        rate: walkRate,
        description: walkDescription,
        // front_camera_price: walkIsAllowFrontCamera ? walkFrontCameraPrice : null,
        // is_allow_front_camera: walkIsAllowFrontCamera,
        // is_allow_control_movement: walkIsAllowControlMovement,
        is_allow_free_mode: walkIsAllowFreeMode,
        status: STATUS_NUMBERS.ONLINE,
        phone_model: await this.phoneInfoService.getDeviceModelWithOS(),
      });
      await firstValueFrom(this.client.coordinatePost(request));
      console.log('🟢 Set online remote status');
      this.setOnlineLocalStatus();
    } catch (error) {
      const responseMessage: string = error?.result?.message ?? error?.result?.error;
      let messageKey: string = responseMessage ?? 'avatar.avatarService.toasts.status.errorSetOnline';

      if (responseMessage && error?.status === HttpStatusCode.NotFound) {
        messageKey = 'avatar.walkDetails.toasts.' + responseMessage; // user_device.not_found
      }
      const errorMessage: string = this.translate.instant(messageKey);
      console.error(errorMessage);
      await this.toastService.error(errorMessage);
      throw error;
    } finally {
      await this.loadingService.stop();
    }
  }

  public async setOfflineRemoteStatus(): Promise<void> {
    try {
      this.inStatusSettingProcess = true;

      const request: CoordinateUpdateRequest = new CoordinateUpdateRequest({
        status: STATUS_NUMBERS.OFFLINE,
      });
      await firstValueFrom(this.client.coordinatePut(request));
      console.log('🔴 Set offline remote status');
      this.setOfflineLocalStatus();
    } catch (error) {
      const responseMessage: string = error?.result?.error ?? error?.result?.message;
      const messageKey: string = responseMessage ?? 'avatar.avatarService.toasts.status.errorSetOffline';

      const errorMessage: string = this.translate.instant(messageKey);
      console.error(errorMessage);
      await this.toastService.error(errorMessage);
      throw error;
    } finally {
      this.inStatusSettingProcess = false;
    }
  }

  //#endregion Status

  public async requestCompassPermission(): Promise<void> {
    if ((typeof window === 'undefined') || !window.DeviceOrientationEvent) return;

    interface DeviceOrientationEventiOS extends DeviceOrientationEvent {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    }

    const requestPermission = (DeviceOrientationEvent as unknown as DeviceOrientationEventiOS).requestPermission;
    const iOS = typeof requestPermission === 'function';
    let compassAccessGranted = true;
    if (iOS) { // iOS 13+
      const response = await requestPermission();
      compassAccessGranted = (response === 'granted');
    }

    if (!compassAccessGranted) {
      console.warn('Compass access was NOT granted!');
      this.toastService.warning(this.translate.instant('avatar.avatarService.toasts.permissions.compassNotGranted'));
    }
    this.isCompassAvailable = undefined; // will be switched to true only for correct rotations on deviceorientation event!
  }

  public async openTutorialModal(): Promise<void> {
    const opts: ModalOptions = {
      component: TutorialControlComponent,
      cssClass: 'tutorial-modal',
    };
    const tutorialModal: HTMLIonModalElement = await this.modalController.create(opts);
    await tutorialModal?.present();
  }

  private async requestBrowserLocationPermission(callback: Function = null): Promise<void> {
    const result: LocationResult = await this.locationAcquisition.acquireExplicit();
    if (result.kind === 'gps') {
      if (typeof callback === 'function') await callback();
      return;
    }

    await this.toastService.warning(
      this.translate.instant('avatar.avatarService.toasts.gps.allowBrowserGeolocation'),
    );
  }
}
